/**
 * Salesforce CRM Integration
 * Implements comprehensive Salesforce integration with OAuth 2.0, REST/SOAP APIs,
 * real-time streaming, and bidirectional sync capabilities
 */

import { BaseCRMIntegration } from '../base/BaseCRMIntegration';
import { 
  IntegrationConfig,
  SyncResult,
  FieldMapping,
  Contact,
  Activity,
  OAuthTokens,
  ValidationResult,
  TestResult,
  ConnectionResult
} from '../../../types/integration';
import { APIGateway } from '../../api/APIGateway';
import { AuthenticationService } from '../../api/AuthenticationService';

export interface SalesforceConfig extends IntegrationConfig {
  instanceUrl: string;
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  sandbox: boolean;
  apiVersion: string;
  streamingEnabled: boolean;
}

export interface SalesforceTokens extends OAuthTokens {
  instanceUrl: string;
  id: string;
  tokenType: string;
  issuedAt: string;
  signature: string;
}

export interface SalesforceContact {
  Id?: string;
  FirstName?: string;
  LastName: string;
  Email?: string;
  Phone?: string;
  Title?: string;
  Account?: {
    Id: string;
    Name: string;
  };
  LeadSource?: string;
  CreatedDate?: string;
  LastModifiedDate?: string;
  // Custom fields will be added dynamically
  [key: string]: any;
}

export interface SalesforceActivity {
  Id?: string;
  WhoId: string; // Contact/Lead ID
  Subject: string;
  ActivityDate: string;
  Status: string;
  Type: string;
  Description?: string;
  EmailMessageId?: string;
  CreatedDate?: string;
}

export class SalesforceIntegration extends BaseCRMIntegration {
  private apiGateway: APIGateway;
  private authService: AuthenticationService;
  private tokens: SalesforceTokens | null = null;
  private streamingClient: any = null; // Will be initialized when streaming is enabled
  
  constructor(config: SalesforceConfig) {
    super(config);
    this.apiGateway = APIGateway.getInstance();
    this.authService = new AuthenticationService();
  }

  // Required methods from BaseIntegration
  async connect(): Promise<ConnectionResult> {
    this.status = 'configuring';
    try {
      const testResult = await this.testConnection();
      this.status = 'active';
      return {
        success: testResult.success,
        message: testResult.message,
        connectionId: this.config.id,
        capabilities: this.getSupportedFeatures(),
        metadata: { platform: 'salesforce', version: 'v58.0' }
      };
    } catch (error) {
      this.status = 'error';
      return {
        success: false,
        message: `Connection failed: ${error.message}`,
        connectionId: this.config.id
      };
    }
  }

  private getSupportedFeatures(): string[] {
    return [
      'contacts_sync',
      'activities_logging',
      'custom_fields',
      'real_time_updates',
      'oauth_authentication',
      'streaming_api',
      'field_mapping',
      'conflict_resolution'
    ];
  }

  async disconnect(): Promise<void> {
    this.status = 'inactive';
    this.tokens = null;
    if (this.streamingClient) {
      // Disconnect streaming client
      this.streamingClient = null;
    }
  }

  async validateConfig(): Promise<ValidationResult> {
    const config = this.config as SalesforceConfig;
    const errors: string[] = [];

    if (!config.clientId) {
      errors.push('Client ID is required');
    }
    
    if (!config.clientSecret) {
      errors.push('Client Secret is required');
    }
    
    if (!config.redirectUri) {
      errors.push('Redirect URI is required');
    }

    return {
      valid: errors.length === 0,
      errors: errors.map(error => ({
        field: 'config',
        message: error,
        code: 'VALIDATION_ERROR'
      })),
      warnings: []
    };
  }

  /**
   * Initialize OAuth 2.0 authentication flow for Salesforce
   */
  async authenticate(): Promise<ValidationResult> {
    try {
      const _config = this.config as SalesforceConfig;
      
      // Generate OAuth authorization URL
      const authUrl = this.buildAuthorizationUrl();
      
      return {
        valid: true,
        errors: [],
        warnings: [],
        data: { authUrl }
      } as any;
    } catch (error) {
      return {
        valid: false,
        errors: [{
          field: 'authentication',
          message: `Authentication failed: ${error.message}`,
          code: 'AUTH_ERROR'
        }],
        warnings: []
      };
    }
  }

  /**
   * Complete OAuth flow with authorization code
   */
  async completeAuthentication(authCode: string): Promise<ValidationResult> {
    try {
      const config = this.config as SalesforceConfig;
      const tokenUrl = `${this.getBaseUrl()}/services/oauth2/token`;
      
      const tokenResponse = await this.apiGateway.processRequest({
        method: 'POST',
        url: tokenUrl,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: config.clientId,
          client_secret: config.clientSecret,
          redirect_uri: config.redirectUri,
          code: authCode
        }).toString()
      });

      if (tokenResponse.status === 200) {
        // Map the response to our token format
        const tokenData = tokenResponse.data;
        this.tokens = {
          accessToken: tokenData.access_token,
          refreshToken: tokenData.refresh_token,
          instanceUrl: tokenData.instance_url,
          id: tokenData.id,
          tokenType: tokenData.token_type,
          issuedAt: tokenData.issued_at,
          signature: tokenData.signature
        };
        this.status = 'active';
        
        // Store tokens securely
        await this.storeTokens(this.config.id, this.tokens);
        
        return {
          valid: true,
          errors: [],
          warnings: []
        };
      } else {
        throw new Error(`Token exchange failed: ${tokenResponse.status}`);
      }
    } catch (error) {
      return {
        valid: false,
        errors: [{
          field: 'authentication',
          message: `Authentication completion failed: ${error.message}`,
          code: 'AUTH_ERROR'
        }],
        warnings: []
      };
    }
  }

  /**
   * Test connection to Salesforce
   */
  async testConnection(): Promise<TestResult> {
    const startTime = Date.now();
    
    try {
      if (!this.tokens) {
        const storedTokens = await this.getTokens(this.config.id);
        if (!storedTokens) {
          return {
            success: false,
            message: 'No authentication tokens found',
            duration: Date.now() - startTime,
            details: { error: 'Authentication required' }
          };
        }
        this.tokens = storedTokens as SalesforceTokens;
      }

      // Test with a simple query
      const response = await this.makeAuthenticatedRequest(
        'GET',
        '/services/data/v58.0/sobjects/Contact/describe'
      );

      if (response.status === 200) {
        return {
          success: true,
          message: 'Connection test successful',
          duration: Date.now() - startTime,
          details: { responseTime: response.duration }
        };
      } else {
        throw new Error(`Connection test failed: ${response.status}`);
      }
    } catch (error) {
      return {
        success: false,
        message: `Connection test failed: ${error.message}`,
        duration: Date.now() - startTime,
        details: { error: error.message }
      };
    }
  }



  /**
   * Push contacts to Salesforce
   */
  async pushContacts(contacts: Contact[]): Promise<SyncResult> {
    try {
      const salesforceContacts = contacts.map(this.mapContactToSalesforceContact.bind(this));
      const results = [];
      const errors = [];

      for (const contact of salesforceContacts) {
        try {
          let response;
          const sfContact = contact as SalesforceContact;
          if (sfContact.Id) {
            // Update existing contact
            response = await this.makeAuthenticatedRequest(
              'PATCH',
              `/services/data/v58.0/sobjects/Contact/${sfContact.Id}`,
              contact
            );
          } else {
            // Create new contact
            response = await this.makeAuthenticatedRequest(
              'POST',
              '/services/data/v58.0/sobjects/Contact',
              contact
            );
          }
          
          if (response.status >= 200 && response.status < 300) {
            results.push(response.data);
          } else {
            errors.push(`Contact sync failed: ${response.status}`);
          }
        } catch (error) {
          errors.push(`Contact sync error: ${error.message}`);
        }
      }

      return {
        success: errors.length === 0,
        recordsProcessed: contacts.length,
        recordsSucceeded: results.length,
        recordsFailed: errors.length,
        data: results,
        errors
      };
    } catch (error) {
      return {
        success: false,
        recordsProcessed: contacts.length,
        recordsSucceeded: 0,
        recordsFailed: contacts.length,
        data: [],
        errors: [error.message]
      };
    }
  }

  /**
   * Log multiple email activities to Salesforce (base class override)
   */
  async logEmailActivities(activities: any[]): Promise<void> {
    const result = await this.logEmailActivitiesInternal(activities);
    if (!result.success) {
      throw new Error(`Activity logging failed: ${result.errors.join(', ')}`);
    }
  }

  /**
   * Internal method to log multiple email activities to Salesforce
   */
  private async logEmailActivitiesInternal(activities: Activity[]): Promise<SyncResult> {
    try {
      const salesforceActivities = activities.map(this.mapActivityToSalesforceActivity.bind(this));
      const results = [];
      const errors = [];

      for (const activity of salesforceActivities) {
        try {
          const response = await this.makeAuthenticatedRequest(
            'POST',
            '/services/data/v58.0/sobjects/Task',
            activity
          );
          
          if (response.status >= 200 && response.status < 300) {
            results.push(response.data);
          } else {
            errors.push(`Activity logging failed: ${response.status}`);
          }
        } catch (error) {
          errors.push(`Activity logging error: ${error.message}`);
        }
      }

      return {
        success: errors.length === 0,
        recordsProcessed: activities.length,
        recordsSucceeded: results.length,
        recordsFailed: errors.length,
        data: results,
        errors
      };
    } catch (error) {
      return {
        success: false,
        recordsProcessed: activities.length,
        recordsSucceeded: 0,
        recordsFailed: activities.length,
        data: [],
        errors: [error.message]
      };
    }
  }



  /**
   * Initialize Salesforce Streaming API for real-time updates
   */
  async initializeStreaming(): Promise<void> {
    const config = this.config as SalesforceConfig;
    if (!config.streamingEnabled || !this.tokens) {
      return;
    }

    try {
      // This would typically use a library like faye or jsforce for streaming
      // For now, we'll set up the basic structure
      console.log('Initializing Salesforce Streaming API...');
      
      // Subscribe to Contact changes
      await this.subscribeToContactChanges();
      
      console.log('Salesforce Streaming API initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Salesforce Streaming API:', error);
    }
  }

  // Private helper methods

  private buildAuthorizationUrl(): string {
    const config = this.config as SalesforceConfig;
    const baseUrl = this.getBaseUrl();
    
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      scope: 'api refresh_token offline_access'
    });

    return `${baseUrl}/services/oauth2/authorize?${params.toString()}`;
  }

  private getBaseUrl(): string {
    const config = this.config as SalesforceConfig;
    return config.sandbox ? 'https://test.salesforce.com' : 'https://login.salesforce.com';
  }

  private async makeAuthenticatedRequest(method: string, endpoint: string, data?: any) {
    if (!this.tokens) {
      throw new Error('No authentication tokens available');
    }

    const url = `${this.tokens.instanceUrl}${endpoint}`;
    
    return this.apiGateway.processRequest({
      method: method as any,
      url,
      headers: {
        'Authorization': `Bearer ${this.tokens.accessToken}`,
        'Content-Type': 'application/json'
      },
      body: data
    });
  }

  private buildContactQuery(lastSyncTime?: Date): string {
    let query = `
      SELECT Id, FirstName, LastName, Email, Phone, Title, 
             Account.Id, Account.Name, LeadSource, 
             CreatedDate, LastModifiedDate
      FROM Contact
    `;

    if (lastSyncTime) {
      const isoTime = lastSyncTime.toISOString();
      query += ` WHERE LastModifiedDate > ${isoTime}`;
    }

    query += ' ORDER BY LastModifiedDate DESC LIMIT 1000';
    
    return query;
  }

  private mapSalesforceContactToContact(sfContact: SalesforceContact): Contact {
    return {
      id: sfContact.Id,
      externalId: sfContact.Id,
      firstName: sfContact.FirstName || '',
      lastName: sfContact.LastName,
      email: sfContact.Email || '',
      phone: sfContact.Phone || '',
      title: sfContact.Title || '',
      company: sfContact.Account?.Name || '',
      source: sfContact.LeadSource || '',
      createdAt: sfContact.CreatedDate ? new Date(sfContact.CreatedDate) : new Date(),
      updatedAt: sfContact.LastModifiedDate ? new Date(sfContact.LastModifiedDate) : new Date(),
      customFields: this.extractCustomFields(sfContact)
    };
  }

  private mapContactToSalesforceContact(contact: Contact): SalesforceContact {
    const sfContact: SalesforceContact = {
      FirstName: contact.firstName,
      LastName: contact.lastName,
      Email: contact.email,
      Phone: contact.phone,
      Title: contact.title,
      LeadSource: contact.source
    };

    if (contact.externalId) {
      sfContact.Id = contact.externalId;
    }

    // Add custom fields
    if (contact.customFields) {
      Object.assign(sfContact, contact.customFields);
    }

    return sfContact;
  }

  private mapActivityToSalesforceActivity(activity: Activity): SalesforceActivity {
    return {
      WhoId: activity.contactId,
      Subject: activity.subject,
      ActivityDate: activity.date.toISOString().split('T')[0],
      Status: 'Completed',
      Type: this.mapActivityType(activity.type),
      Description: activity.description,
      EmailMessageId: activity.emailMessageId
    };
  }

  private mapActivityType(type: string): string {
    const typeMap: { [key: string]: string } = {
      'email_open': 'Email',
      'email_click': 'Email',
      'email_sent': 'Email',
      'email_bounce': 'Email',
      'email_unsubscribe': 'Email'
    };
    
    return typeMap[type] || 'Other';
  }

  private mapSalesforceFieldType(sfType: string): string {
    const typeMap: { [key: string]: string } = {
      'string': 'text',
      'textarea': 'textarea',
      'email': 'email',
      'phone': 'phone',
      'url': 'url',
      'date': 'date',
      'datetime': 'datetime',
      'boolean': 'boolean',
      'int': 'number',
      'double': 'number',
      'currency': 'currency',
      'percent': 'percent',
      'picklist': 'select',
      'multipicklist': 'multiselect'
    };
    
    return typeMap[sfType] || 'text';
  }

  private extractCustomFields(sfContact: SalesforceContact): { [key: string]: any } {
    const customFields: { [key: string]: any } = {};
    
    Object.keys(sfContact).forEach(key => {
      if (key.endsWith('__c')) {
        customFields[key] = sfContact[key];
      }
    });
    
    return customFields;
  }

  private async subscribeToContactChanges(): Promise<void> {
    // This would implement the actual streaming subscription
    // For now, we'll just log the intent
    console.log('Subscribing to Salesforce Contact changes via Streaming API');
    
    // In a real implementation, this would:
    // 1. Create a PushTopic for Contact changes
    // 2. Subscribe to the streaming channel
    // 3. Handle incoming change notifications
    // 4. Trigger sync operations based on changes
  }

  // Required methods from BaseCRMIntegration

  async syncActivities(activities: any[]): Promise<void> {
    await this.logEmailActivities(activities);
  }

  // Override logEmailActivity to match base class signature (single activity)
  async logEmailActivity(activity: any): Promise<void> {
    await this.logEmailActivities([activity]);
  }

  async getContact(contactId: string): Promise<any | null> {
    try {
      const response = await this.makeAuthenticatedRequest(
        'GET',
        `/services/data/v58.0/sobjects/Contact/${contactId}`
      );

      if (response.status === 200) {
        return this.mapSalesforceContactToContact(response.data);
      } else if (response.status === 404) {
        return null;
      } else {
        throw new Error(`Failed to get contact: ${response.status}`);
      }
    } catch (error) {
      console.error('Error getting contact:', error);
      return null;
    }
  }

  async createContact(contact: Partial<any>): Promise<any> {
    const sfContact = this.mapContactToSalesforceContact(contact as Contact);
    delete sfContact.Id; // Remove ID for creation

    const response = await this.makeAuthenticatedRequest(
      'POST',
      '/services/data/v58.0/sobjects/Contact',
      sfContact
    );

    if (response.status >= 200 && response.status < 300) {
      return { ...contact, id: response.data.id, externalId: response.data.id };
    } else {
      throw new Error(`Failed to create contact: ${response.status}`);
    }
  }

  async updateContact(contactId: string, updates: Partial<any>): Promise<any> {
    const sfUpdates = this.mapContactToSalesforceContact(updates as Contact);
    delete sfUpdates.Id; // Remove ID for update

    const response = await this.makeAuthenticatedRequest(
      'PATCH',
      `/services/data/v58.0/sobjects/Contact/${contactId}`,
      sfUpdates
    );

    if (response.status >= 200 && response.status < 300) {
      return { ...updates, id: contactId, externalId: contactId };
    } else {
      throw new Error(`Failed to update contact: ${response.status}`);
    }
  }

  async deleteContact(contactId: string): Promise<void> {
    const response = await this.makeAuthenticatedRequest(
      'DELETE',
      `/services/data/v58.0/sobjects/Contact/${contactId}`
    );

    if (response.status < 200 || response.status >= 300) {
      throw new Error(`Failed to delete contact: ${response.status}`);
    }
  }

  async searchContacts(query: string, limit?: number): Promise<any[]> {
    const soqlQuery = `
      SELECT Id, FirstName, LastName, Email, Phone, Title, 
             Account.Id, Account.Name, LeadSource, 
             CreatedDate, LastModifiedDate
      FROM Contact
      WHERE Email LIKE '%${query}%' OR FirstName LIKE '%${query}%' OR LastName LIKE '%${query}%'
      ORDER BY LastModifiedDate DESC
      LIMIT ${limit || 100}
    `;

    const response = await this.makeAuthenticatedRequest(
      'GET',
      `/services/data/v58.0/query?q=${encodeURIComponent(soqlQuery)}`
    );

    if (response.status === 200) {
      const salesforceContacts = response.data.records as SalesforceContact[];
      return salesforceContacts.map(this.mapSalesforceContactToContact.bind(this));
    } else {
      throw new Error(`Contact search failed: ${response.status}`);
    }
  }

  // Override method signatures to match base class
  async syncContacts(options: any): Promise<any> {
    const lastSyncTime = options?.dateRange?.start;
    return await this.syncContactsInternal(lastSyncTime);
  }

  private async syncContactsInternal(lastSyncTime?: Date): Promise<SyncResult> {
    try {
      const query = this.buildContactQuery(lastSyncTime);
      const response = await this.makeAuthenticatedRequest(
        'GET',
        `/services/data/v58.0/query?q=${encodeURIComponent(query)}`
      );

      if (response.status !== 200) {
        throw new Error(`Contact sync failed: ${response.status}`);
      }

      const salesforceContacts = response.data.records as SalesforceContact[];
      const contacts = salesforceContacts.map(this.mapSalesforceContactToContact.bind(this));

      return {
        success: true,
        recordsProcessed: contacts.length,
        recordsSucceeded: contacts.length,
        recordsFailed: 0,
        data: contacts,
        errors: []
      };
    } catch (error) {
      return {
        success: false,
        recordsProcessed: 0,
        recordsSucceeded: 0,
        recordsFailed: 0,
        data: [],
        errors: [error.message]
      };
    }
  }

  // Override getCustomFields to return FieldMapping[]
  async getCustomFields(): Promise<FieldMapping[]> {
    try {
      const response = await this.makeAuthenticatedRequest(
        'GET',
        '/services/data/v58.0/sobjects/Contact/describe'
      );

      if (response.status !== 200) {
        throw new Error(`Custom fields retrieval failed: ${response.status}`);
      }

      const fields = response.data.fields || [];
      return fields
        .filter((field: any) => field.custom)
        .map((field: any) => ({
          id: field.name,
          sourceField: field.name,
          targetField: field.name,
          direction: 'bidirectional' as any,
          required: !field.nillable,
          dataType: this.mapSalesforceFieldTypeToDataType(field.type)
        }));
    } catch (error) {
      console.error('Error retrieving custom fields:', error);
      return [];
    }
  }

  private mapSalesforceFieldTypeToDataType(sfType: string): 'string' | 'number' | 'date' | 'boolean' | 'object' | 'array' {
    const typeMap: { [key: string]: 'string' | 'number' | 'date' | 'boolean' | 'object' | 'array' } = {
      'string': 'string',
      'textarea': 'string',
      'email': 'string',
      'phone': 'string',
      'url': 'string',
      'date': 'date',
      'datetime': 'date',
      'boolean': 'boolean',
      'int': 'number',
      'double': 'number',
      'currency': 'number',
      'percent': 'number',
      'picklist': 'string',
      'multipicklist': 'array'
    };
    
    return typeMap[sfType] || 'string';
  }

  // Abstract method implementations from base class
  protected extractWebhookEventType(payload: any): string {
    return payload.eventType || payload.type || 'unknown';
  }

  protected async handleContactWebhook(payload: any): Promise<void> {
    const contactId = payload.sobject?.Id || payload.contactId;
    if (contactId) {
      await this.syncContactFromCRM(contactId);
    }
  }

  protected async handleContactDeletedWebhook(payload: any): Promise<void> {
    const contactId = payload.sobject?.Id || payload.contactId;
    if (contactId) {
      await this.emitEvent('contact.deleted', { contactId, source: 'salesforce' });
    }
  }

  protected async updateEmailPlatformContact(contact: any): Promise<any> {
    // This would integrate with the email platform's API
    // For now, we'll just return the contact as-is
    return contact;
  }

  /**
   * Refresh OAuth tokens when they expire
   */
  private async refreshTokens(): Promise<void> {
    if (!this.tokens?.refreshToken) {
      throw new Error('No refresh token available');
    }

    const config = this.config as SalesforceConfig;
    const tokenUrl = `${this.getBaseUrl()}/services/oauth2/token`;
    
    const response = await this.apiGateway.processRequest({
      method: 'POST',
      url: tokenUrl,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: config.clientId,
        client_secret: config.clientSecret,
        refresh_token: this.tokens.refreshToken
      }).toString()
    });

    if (response.status === 200) {
      this.tokens = { ...this.tokens, ...response.data };
      await this.storeTokens(this.config.id, this.tokens);
    } else {
      throw new Error(`Token refresh failed: ${response.status}`);
    }
  }

  // Token storage methods (temporary implementation)
  private async storeTokens(integrationId: string, tokens: any): Promise<void> {
    // Store tokens in memory for now - in production this would use secure storage
    (this.authService as any)._tokenCache = (this.authService as any)._tokenCache || new Map();
    (this.authService as any)._tokenCache.set(integrationId, tokens);
  }

  private async getTokens(integrationId: string): Promise<any> {
    // Get tokens from memory - in production this would use secure storage
    const cache = (this.authService as any)._tokenCache;
    return cache ? cache.get(integrationId) : null;
  }
}